import { TransformPayloadReturnT, instanceMainService, transformPayload } from 'primsell-shared-layer/api'

import {
  WrappedResponse,
  IBaseReward,
  IRequestGetFilteredRewardsIdNameList,
  IResponseGetFilteredRewardsIdNameList,
  IResponseGenerateRewardTokenId,
  IResponseGetRewardMetadata,
  ICreateRedeemReward,
  ICreateEventReward,
  IFullReward,
  IGetRewardsRequest,
  IResponse,
  IRequestPublicRewardByCode,
  IPublicReward,
  IUpdateRedeemReward,
  IUpdateEventReward,
  IRewardDropData,
  IRequestGetRewardsDropsList,
  IRequestGetRewardsSalesList,
  IRewardSalesData,
} from 'primsell-shared-layer/types'

import { REWARDS_URLS } from './rewards.constants'

import { getLink } from 'primsell-shared-layer/helpers'
import { AxiosResponse } from 'axios'
import { replaceUrlParams } from '../../utils'

export class RewardsAPI {
  static getRewardDetailsById(id: IFullReward['id']): Promise<AxiosResponse<IResponse<IFullReward>>> {
    return instanceMainService.get(replaceUrlParams(REWARDS_URLS.REWARD_BY_ID, { id }))
  }

  static getRewards(payload: IGetRewardsRequest): Promise<WrappedResponse<IBaseReward[]>> {
    const transformedPayload: TransformPayloadReturnT = transformPayload(payload)

    return instanceMainService.get(REWARDS_URLS.REWARDS_LIST, { params: transformedPayload })
  }

  static getPublicRewardDetailsByCode(payload: IRequestPublicRewardByCode): Promise<WrappedResponse<IPublicReward>> {
    return instanceMainService.get(replaceUrlParams(REWARDS_URLS.REWARD_PUBLIC_BY_CODE, { code: payload.code }))
  }

  static getFilteredRewardsIdNameList(
    payload: IRequestGetFilteredRewardsIdNameList
  ): Promise<WrappedResponse<IResponseGetFilteredRewardsIdNameList[]>> {
    const transformedPayload: TransformPayloadReturnT = transformPayload(payload)

    return instanceMainService.get(REWARDS_URLS.REWARDS_FILTERED_LIST, { params: transformedPayload })
  }

  static getPublicReward(id: string): Promise<WrappedResponse<IBaseReward>> {
    return instanceMainService.get(getLink(REWARDS_URLS.PUBLIC_REWARD, { id }))
  }

  static getRewardMetadata(id: string): Promise<WrappedResponse<IResponseGetRewardMetadata>> {
    return instanceMainService.get(getLink(REWARDS_URLS.REWARD_METADATA, { id }))
  }

  static generateTokenId(): Promise<WrappedResponse<IResponseGenerateRewardTokenId>> {
    return instanceMainService.post(REWARDS_URLS.GENERATE_REWARD_TOKEN_ID)
  }

  static createDiscountReward(payload: ICreateRedeemReward): Promise<IFullReward> {
    return instanceMainService.post(REWARDS_URLS.DISCOUNT_REWARD, payload)
  }

  static updateDiscountReward({ id, ...payload }: IUpdateRedeemReward): Promise<IFullReward> {
    return instanceMainService.patch(getLink(REWARDS_URLS.DISCOUNT_REWARD_ID, { id }), payload)
  }

  static createFreeProductReward(payload: ICreateRedeemReward): Promise<IFullReward> {
    return instanceMainService.post(REWARDS_URLS.FREE_PRODUCT_REWARD, payload)
  }

  static updateFreeProductReward({ id, ...payload }: IUpdateRedeemReward): Promise<IFullReward> {
    return instanceMainService.patch(getLink(REWARDS_URLS.FREE_PRODUCT_REWARD_ID, { id }), payload)
  }

  static createExclusiveAccessReward(payload: ICreateRedeemReward): Promise<IFullReward> {
    return instanceMainService.post(REWARDS_URLS.EXCLUSIVE_ACCESS_REWARD, payload)
  }

  static updateExclusiveAccessReward({ id, ...payload }: IUpdateRedeemReward): Promise<IFullReward> {
    return instanceMainService.patch(getLink(REWARDS_URLS.EXCLUSIVE_ACCESS_REWARD_ID, { id }), payload)
  }

  static createEventReward(payload: ICreateEventReward): Promise<IFullReward> {
    return instanceMainService.post(REWARDS_URLS.EVENT_REWARD, payload)
  }

  static updateEventReward({ id, ...payload }: IUpdateEventReward): Promise<IFullReward> {
    return instanceMainService.patch(getLink(REWARDS_URLS.EVENT_REWARD_ID, { id }), payload)
  }

  static deleteReward(id: string): Promise<WrappedResponse<IFullReward>> {
    return instanceMainService.delete(getLink(REWARDS_URLS.REWARD_BY_ID, { id }))
  }

  static generateEventValidationCode(id: string): Promise<void> {
    return instanceMainService.post(getLink(REWARDS_URLS.GENERATE_EVENT_VALIDATION_CODE, { id }))
  }

  static getDrops(payload: IRequestGetRewardsDropsList): Promise<WrappedResponse<IRewardDropData[]>> {
    const transformedPayload: TransformPayloadReturnT = transformPayload(payload)

    return instanceMainService.get(REWARDS_URLS.DROPS, { params: transformedPayload })
  }

  static getDropById(id: string): Promise<WrappedResponse<IRewardDropData>> {
    return instanceMainService.get(getLink(REWARDS_URLS.DROP_BY_ID, { id }))
  }

  static getDropsCSV(
    // TODO Exclude<IRequestGetRewardsDropsList, 'pagination'>
    payload: any
  ): Promise<WrappedResponse<IRewardDropData[]>> {
    const transformedPayload: TransformPayloadReturnT = transformPayload(payload)

    return instanceMainService.get(REWARDS_URLS.DROPS_CSV, { params: transformedPayload })
  }

  static getSales(payload: IRequestGetRewardsSalesList): Promise<WrappedResponse<IRewardSalesData[]>> {
    const transformedPayload: TransformPayloadReturnT = transformPayload(payload)

    return instanceMainService.get(REWARDS_URLS.SALES, { params: transformedPayload })
  }

  static getSalesById(id: string): Promise<WrappedResponse<IRewardSalesData>> {
    return instanceMainService.get(getLink(REWARDS_URLS.SALES_BY_ID, { id }))
  }

  static getSalesCSV(
    // TODO Exclude<IRequestGetRewardsSalesList, 'pagination'>
    payload: any
  ): Promise<WrappedResponse<IRewardSalesData[]>> {
    const transformedPayload: TransformPayloadReturnT = transformPayload(payload)

    return instanceMainService.get(REWARDS_URLS.SALES_CSV, { params: transformedPayload })
  }
}
